import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Skeleton from '@material-ui/lab/Skeleton';
import NotesInput from './NotesInput';
import { AddressButton } from './AddressButton';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@material-ui/core/Link';

const MILISECONDS_IN_DAY = 1000 * 3600 * 24;
export const PRINT_MODE = 'print';
export const PREVIEW_MODE = 'preview';
export const SUMMARY_REPORT_MODE_TYPE = PropTypes.oneOf([
  PRINT_MODE,
  PREVIEW_MODE,
]);

const useStyles = makeStyles({
  root: {
    '& button': {
      background: 'none!important',
      border: 'none',
      padding: '0!important',
      color: '#0000cd',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    maxHeight: '100%',
  },
  '@global': {
    '@media print': {
      '*': {
        color: '#000 !important',
        backgroundColor: 'transparent !important',
        boxShadow: 'none !important',
      },
      'a': {
        color: '#000 !important',
        textDecoration: 'underline',
      },
      'html, body': {
        height: 'auto !important',
        overflow: 'visible !important',
        position: 'static !important',
      },
    },
  },
});

const StyledTableCellWork = withStyles({
  root: {
    padding: '4px',
    color: 'green',
  },
})(TableCell);

const StyledTableCellRest = withStyles({
  root: {
    padding: '4px',
    color: 'grey',
  },
})(TableCell);

const StyledTableCellOvertime = withStyles({
  root: {
    padding: '4px',
    color: 'grey',
  },
})(TableCell);

const StyledTableCell = withStyles({
  root: {
    padding: '4px',
  },
})(TableCell);

const hasOvertime = overtime => {
  return overtime !== '00:00:00';
};

const SkeletonTableRow = () => {
  return (
    <TableRow>
      <StyledTableCell component='th' scope='row'>
        <Skeleton variant='text' />
      </StyledTableCell>
      <StyledTableCellRest align='right'>
        <Skeleton variant='text' />
      </StyledTableCellRest>
      <StyledTableCellRest align='right'>
        <Skeleton variant='text' />
      </StyledTableCellRest>
      <StyledTableCellWork align='right'>
        <Skeleton variant='text' />
      </StyledTableCellWork>
      <StyledTableCellWork align='right'>
        <Skeleton variant='text' />
      </StyledTableCellWork>
      <StyledTableCellWork align='right'>
        <Skeleton variant='text' />
      </StyledTableCellWork>
      <StyledTableCell align='right'>
        <Skeleton variant='text' />
      </StyledTableCell>
    </TableRow>
  );
};

const PrintTableRow = ({
  style,
  day,
  distance, 
  restHours, 
  workHours, 
  overtime,
  addressMap,
  onclick,
  options,
  period,
  nextPeriod,
}) => {
  const { hasNotesColumn, hasWorktime } = options;
  const beforeWorkAddress = addressMap ? addressMap.get(period.coordinates) : '???';
  const afterWorkAddress = addressMap ? addressMap.get(nextPeriod.coordinates) : '???';

  return (
    <React.Fragment>
      <TableRow style={style}>
        <StyledTableCell component='th' scope='row'>
          {day}
        </StyledTableCell>
        <StyledTableCellRest align='right'>{restHours}</StyledTableCellRest>
        <StyledTableCellWork align='right'>{workHours}</StyledTableCellWork>
        <StyledTableCellWork align='right'>{distance}</StyledTableCellWork>
        {hasWorktime && (
          <StyledTableCellOvertime align='right'></StyledTableCellOvertime>
        )}
        <StyledTableCell align='right'>
          <AddressButton
            address={beforeWorkAddress}
            coordinates={period.coordinates}
            onclick={onclick}
          />
        </StyledTableCell>
        {hasNotesColumn && (
          <StyledTableCell>
            <NotesInput />
          </StyledTableCell>
        )}
      </TableRow>
      <TableRow style={style}>
        <StyledTableCell component='th' scope='row'></StyledTableCell>
        <StyledTableCellRest align='right'></StyledTableCellRest>
        <StyledTableCellWork align='right'></StyledTableCellWork>
        <StyledTableCellWork align='right'></StyledTableCellWork>
        {hasWorktime && (
          <StyledTableCellOvertime align='right'>
            {hasOvertime(overtime) && overtime}
          </StyledTableCellOvertime>
        )}
        <StyledTableCell align='right'>
          {workHours && afterWorkAddress && (
            <AddressButton
              address={afterWorkAddress}
              coordinates={nextPeriod.coordinates}
              onclick={onclick}
            />
          )}
        </StyledTableCell>
        {hasNotesColumn && (
          <StyledTableCell>
            <NotesInput />
          </StyledTableCell>
        )}
      </TableRow>
    </React.Fragment>
  );
};

PrintTableRow.propTypes = {
  style: PropTypes.object,
  period: PropTypes.shape({
    restHours: PropTypes.string,
    workHours: PropTypes.string,
    distance: PropTypes.number,
    rest: PropTypes.string,
    work: PropTypes.string,
    keyIndex: PropTypes.number,
    overtime: PropTypes.string,
  }),
  options: PropTypes.object,
  nextPeriod: PropTypes.shape({
    keyIndex: PropTypes.number,
  }),
  addressMap: PropTypes.object,
  onclick: PropTypes.func,
  day: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

const PrintSummaryReport = ({
  id,
  object,
  addressMap,
  loading,
  onSelectItem,
  options = {},
  mode = PREVIEW_MODE,
}) => {
  const { hasNotesColumn, hasWorktime } = options;
  const classes = useStyles();
  const countSkeletonItems = 3;
  let tableBody;
  if (loading) {
    tableBody = Array.from(new Array(countSkeletonItems)).map((item, index) => {
      return <SkeletonTableRow key={index} />;
    });
  } else {
    const { objectId } = object;
    tableBody = object.days.map((dayGroup, dayIndex) => {
      const { day, dayStart, rest, work, dist, overtime } = dayGroup;
      const dayEnd = new Date(dayStart);
      dayEnd.setMilliseconds(MILISECONDS_IN_DAY - 1);
      const firstPeriod = dayGroup.periods[0];
      const lastPeriod = dayGroup.periods[dayGroup.periods.length - 1];
      const dayComponent =
        mode === PRINT_MODE ? (
          day
        ) : (
          <Link
            component={RouterLink}
            target='_blank'
            to={`/report/route-sheet?objectId=${objectId}&from=${dayStart.toISOString()}&to=${dayEnd.toISOString()}&type=detailed`}
          >
            {day}
          </Link>
        );

      let firstRow = [
        <PrintTableRow
          key={-dayIndex}
          day={dayComponent}
          distance={dist}
          restHours={rest}
          workHours={work}
          overtime={overtime}
          addressMap={addressMap}
          onclick={onSelectItem}
          options={options}
          period={firstPeriod}
          nextPeriod={lastPeriod}
        />
      ];

      return firstRow;
    });
  }

  return (
    <TableContainer className={classes.root} id={id} component={Paper}>
      <Table size='small' aria-label='print report table' stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell>Ден</StyledTableCell>
            <StyledTableCell align='right'>Престой</StyledTableCell>
            <StyledTableCell align='right'>Моточасове</StyledTableCell>
            <StyledTableCell align='right'>Пробег(км)</StyledTableCell>
            {hasWorktime && (
              <StyledTableCell align='right'>Изв.труд</StyledTableCell>
            )}
            <StyledTableCell align='right'>Населено Място</StyledTableCell>
            {hasNotesColumn && (
              <StyledTableCell align='right'>Бележки</StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>{tableBody}</TableBody>
      </Table>
    </TableContainer>
  );
};

PrintSummaryReport.propTypes = {
  object: PropTypes.object,
  loading: PropTypes.bool,
  addressMap: PropTypes.object,
  onSelectItem: PropTypes.func,
  options: PropTypes.object,
  id: PropTypes.string,
  mode: SUMMARY_REPORT_MODE_TYPE,
};

export default PrintSummaryReport;
